import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Info,
  Target,
  Zap,
  CheckCircle2,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { ModalShell } from './ui';
import { getPageInfo, PageInfoConfig } from '../../constants/pageDescriptions';
import '../../styles/pageInfoModal.css';

interface PageInfoModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  path?: string;
}

export const openPageInfoModal = (path?: string) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('open-page-info-modal', { detail: { path } }));
  }
};

export const PageInfoModal: React.FC<PageInfoModalProps> = ({
  isOpen: controlledIsOpen,
  onClose: controlledOnClose,
  path: controlledPath,
}) => {
  const location = useLocation();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [activePath, setActivePath] = useState<string>(controlledPath || location.pathname);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const handleClose = useCallback(() => {
    if (isControlled && controlledOnClose) {
      controlledOnClose();
    } else {
      setInternalIsOpen(false);
    }
  }, [isControlled, controlledOnClose]);

  useEffect(() => {
    const handleOpenEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{ path?: string }>;
      setActivePath(customEvent.detail?.path || location.pathname);
      setInternalIsOpen(true);
    };

    window.addEventListener('open-page-info-modal', handleOpenEvent);
    return () => window.removeEventListener('open-page-info-modal', handleOpenEvent);
  }, [location.pathname]);

  // Sync active path with location when navigated while modal is closed
  useEffect(() => {
    if (!internalIsOpen && !controlledPath) {
      setActivePath(location.pathname);
    }
  }, [location.pathname, internalIsOpen, controlledPath]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const pageInfo: PageInfoConfig = getPageInfo(activePath);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title={pageInfo.title}
      subtitle={pageInfo.subtitle}
      badge={
        <span className="ns-page-info-modal__group-tag">
          <Sparkles size={11} aria-hidden="true" />
          <span>{pageInfo.group}</span>
        </span>
      }
      icon={<Info size={22} />}
      overlayClassName="ns-page-info-modal__overlay"
      dialogClassName="ns-page-info-modal"
      headerClassName="ns-page-info-modal__header"
      bodyClassName="ns-page-info-modal__body"
      footerClassName="ns-page-info-modal__footer"
      footerLeft={
        pageInfo.playbookSectionId ? (
          <Link
            to={`/guide?section=${pageInfo.playbookSectionId}`}
            className="ns-page-info-guide-link"
            onClick={handleClose}
          >
            <span>Explore in Trading Playbook</span>
            <ArrowRight size={14} />
          </Link>
        ) : (
          <span style={{ fontSize: '0.82rem', color: 'var(--ns-text-muted)' }}>
            NiftySutra Trading Intelligence
          </span>
        )
      }
      footerRight={
        <button
          type="button"
          className="ns-btn ns-btn--primary"
          onClick={handleClose}
        >
          Got it
        </button>
      }
    >
          {/* Section 1: What It Represents */}
          <div className="ns-page-info-overview-card">
            <h3 className="ns-page-info-section-title">
              <Target size={15} />
              <span>What This Page Represents</span>
            </h3>
            <p className="ns-page-info-text">
              {pageInfo.whatItRepresents}
            </p>
          </div>

          {/* Section 2: Why It Matters */}
          {pageInfo.whyItMatters && (
            <div className="ns-page-info-why-card">
              <h3 className="ns-page-info-section-title">
                <Zap size={15} />
                <span>The Institutional Edge (Why It Matters)</span>
              </h3>
              <p className="ns-page-info-text">
                {pageInfo.whyItMatters}
              </p>
            </div>
          )}

          {/* Section 3: Key Metrics & Signals */}
          {pageInfo.keyMetrics.length > 0 && (
            <div>
              <h3 className="ns-page-info-section-title">
                <Sparkles size={15} />
                <span>Key Metrics &amp; How to Read Them</span>
              </h3>
              <div className="ns-page-info-metrics-grid">
                {pageInfo.keyMetrics.map((metric, idx) => (
                  <div key={idx} className="ns-page-info-metric-card">
                    <span className="ns-page-info-metric-label">{metric.label}</span>
                    <p className="ns-page-info-metric-desc">{metric.interpretation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section 4: Actionable Trading Playbook */}
          {pageInfo.actionablePlaybook.length > 0 && (
            <div>
              <h3 className="ns-page-info-section-title">
                <CheckCircle2 size={15} />
                <span>Actionable Trading Rules</span>
              </h3>
              <ul className="ns-page-info-playbook-list">
                {pageInfo.actionablePlaybook.map((rule, idx) => (
                  <li key={idx} className="ns-page-info-playbook-item">
                    <CheckCircle2 size={15} className="ns-page-info-playbook-bullet" />
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Section 5: Pro Tip */}
          {pageInfo.proTip && (
            <div className="ns-page-info-protip">
              <strong>Institutional Pro-Tip: </strong>
              <span>{pageInfo.proTip}</span>
            </div>
          )}
    </ModalShell>
  );
};

export default PageInfoModal;
